import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Tabs, Tooltip as MuiTooltip, Typography, Divider, IconButton } from "@mui/material";
import { fetchInspModels } from "../../services/inspModelCache";
import { useParams } from "react-router-dom";
import { ChevronRightIcon, ExpandLessIcon, ExpandMoreIcon } from "common-lib";
import { ChevronLeftIcon } from "lucide-react";
import { AllInboxIcon } from "../../icons";
import { t } from "i18next";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface InspModelItem {
  id: number;
  uuid?: string;
  name?: string;
  description?: string;
  info: string | null;
}

interface ParsedModel extends InspModelItem {
  epoch?: {
    current: number;
    total: number;
    num_train: number;
  };
  validHistory_id?: number;
  parentId?: number;
}

interface TreeNode {
  id: number;
  children: TreeNode[];
  model?: ParsedModel;
}

interface NodePosition {
  x: number;
  y: number;
  depth: number;
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

/** 깊이별 루트로부터의 반지름 (px) */
const RADII = [0, 160, 295, 420, 530];

const DEPTH_STYLES: Record<number, { bg: string; border: string; color: string }> = {
  0: { bg: "#E6F1FB", border: "#378ADD", color: "#0C447C" },
  1: { bg: "#EEEDFE", border: "#7F77DD", color: "#3C3489" },
  2: { bg: "#E1F5EE", border: "#1D9E75", color: "#085041" },
  3: { bg: "#FAEEDA", border: "#BA7517", color: "#633806" },
  4: { bg: "#FAECE7", border: "#D85A30", color: "#4A1B0C" },
};

const EDGE_COLORS = ["#378ADD", "#7F77DD", "#1D9E75", "#BA7517", "#D85A30"];

const CANVAS_SIZE = 1800;
// const CENTER_X = CANVAS_SIZE / 2;
// const CENTER_Y = CANVAS_SIZE / 2;

// ─────────────────────────────────────────────
// Tree helpers
// ─────────────────────────────────────────────

// info는 문자열(JSON) 또는 객체로 올 수 있음
function parseInfo(info: unknown): any {
  if (!info) return null;
  if (typeof info === "object") return info;
  try {
    return JSON.parse(info as string);
  } catch {
    return null;
  }
}

function buildNodeMap(list: ParsedModel[]): Record<number, TreeNode> {
  const map: Record<number, TreeNode> = {};
  list.forEach((item) => {
    map[item.id] = { id: item.id, children: [], model: item };
  });
  list.forEach((item) => {
    if (item.parentId && map[item.parentId]) {
      map[item.parentId].children.push(map[item.id]);
    }
  });
  return map;
}

function getRoots(map: Record<number, TreeNode>, list: ParsedModel[]): TreeNode[] {
  return list.filter((item) => !item.parentId || !map[item.parentId]).map((item) => map[item.id]);
}

function countVisibleLeaves(node: TreeNode, depth: number, maxDepth: number): number {
  if (depth >= maxDepth || !node.children.length) return 1;
  return node.children.reduce((s, c) => s + countVisibleLeaves(c, depth + 1, maxDepth), 0);
}

function assignRadialPositions(
  node: TreeNode,
  depth: number,
  angleStart: number,
  angleEnd: number,
  cx: number,
  cy: number,
  maxDepth: number,
  out: Record<number, NodePosition>,
) {
  if (depth > maxDepth) return;

  const r = RADII[Math.min(depth, RADII.length - 1)];
  const mid = (angleStart + angleEnd) / 2;
  const x = depth === 0 ? cx : cx + r * Math.cos(mid);
  const y = depth === 0 ? cy : cy + r * Math.sin(mid);
  out[node.id] = { x, y, depth };

  if (depth < maxDepth && node.children.length) {
    const total = node.children.reduce((s, c) => s + countVisibleLeaves(c, depth + 1, maxDepth), 0);
    const span = depth === 0 ? Math.PI * 2 : angleEnd - angleStart;
    let cursor = depth === 0 ? -Math.PI : angleStart;

    node.children.forEach((child) => {
      const share = (countVisibleLeaves(child, depth + 1, maxDepth) / total) * span;
      assignRadialPositions(child, depth + 1, cursor, cursor + share, cx, cy, maxDepth, out);
      cursor += share;
    });
  }
}

// ─────────────────────────────────────────────
// Tooltip
// ─────────────────────────────────────────────

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  model?: ParsedModel;
}

const Tooltip: React.FC<{ state: TooltipState }> = ({ state }) => {
  if (!state.visible || !state.model) return null;
  const m = state.model;
  const rows: [string, string][] = [
    [t("Name"), m.name ?? "-"],
    [t("ID"), String(m.id)],
    [t("Epoch"), String(m.epoch?.current ?? "-")],
    ["validHistory_id", String(m.validHistory_id ?? "-")],
    m.parentId ? [t("ParentID"), String(m.parentId)] : [t("NodeType"), t("Root")],
  ];

  return (
    <div
      style={{
        position: "fixed",
        left: state.x + 14,
        top: state.y - 10,
        background: "var(--dk-surface, #fff)",
        border: "0.5px solid #c0bfb8",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        zIndex: 9999,
        pointerEvents: "none",
        lineHeight: 1.7,
        minWidth: 172,
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "1px 10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <span style={{ color: "var(--dk-text-3, #888)", fontSize: 11 }}>{label}</span>
          <span style={{ fontWeight: 500 }}>{value}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// Edge SVG layer
// ─────────────────────────────────────────────

interface EdgeData {
  fromId: number;
  toId: number;
  depth: number;
}

const EdgeLayer: React.FC<{
  edges: EdgeData[];
  positions: Record<number, NodePosition>;
  w: number;
  h: number;
}> = ({ edges, positions, w, h }) => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: w,
      height: h,
      overflow: "visible",
      pointerEvents: "none",
    }}
  >
    {edges.map(({ fromId, toId, depth }) => {
      const from = positions[fromId];
      const to = positions[toId];
      if (!from || !to) return null;
      const mx = (from.x + to.x) / 2;
      const my = (from.y + to.y) / 2;
      const color = EDGE_COLORS[Math.min(depth, EDGE_COLORS.length - 1)];
      const strokeWidth = Math.max(0.8, 2 - depth * 0.45);
      const opacity = depth === 0 ? 0.55 : 0.3;
      return (
        <path
          key={`${fromId}-${toId}`}
          d={`M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeOpacity={opacity}
        />
      );
    })}
  </svg>
);

// ─────────────────────────────────────────────
// Single node
// ─────────────────────────────────────────────

interface MindMapNodeProps {
  node: TreeNode;
  pos: NodePosition;
  isDimmed: boolean;
  isSelected: boolean;
  onMouseEnter: (e: React.MouseEvent, model: ParsedModel) => void;
  onMouseLeave: () => void;
  onClick: (id: number) => void;
  onDragMove: (id: number, dx: number, dy: number) => void;
}

type TabType = "tree" | "mindmap";

const MindMapNode: React.FC<MindMapNodeProps> = ({
  node,
  pos,
  isDimmed,
  isSelected,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onDragMove,
}) => {
  const depth = Math.min(pos.depth, 4);
  const style = DEPTH_STYLES[depth];
  const dragRef = useRef<{ startX: number; startY: number } | null>(null);
  const hasDragged = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      hasDragged.current = false;
      dragRef.current = { startX: e.clientX, startY: e.clientY };

      const onMove = (me: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = me.clientX - dragRef.current.startX;
        const dy = me.clientY - dragRef.current.startY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasDragged.current = true;
        dragRef.current = { startX: me.clientX, startY: me.clientY };
        onDragMove(node.id, dx, dy);
      };

      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [node.id, onDragMove],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!hasDragged.current) onClick(node.id);
    },
    [node.id, onClick],
  );

  return (
    <div
      data-node="true"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={(e) => node.model && onMouseEnter(e, node.model)}
      onMouseLeave={onMouseLeave}
      style={{
        position: "absolute",
        left: pos.x,
        top: pos.y,
        transform: "translate(-50%, -50%)",
        borderRadius: depth === 0 ? 24 : 20,
        padding: depth === 0 ? "8px 18px" : "5px 13px",
        fontSize: depth === 0 ? 13 : 12,
        fontWeight: 500,
        cursor: "grab",
        border: `${depth === 0 ? "1px" : "0.5px"} solid ${style.border}`,
        background: style.bg,
        color: style.color,
        textAlign: "center",
        whiteSpace: "nowrap",
        lineHeight: 1.5,
        opacity: isDimmed ? 0.15 : 1,
        boxShadow: isSelected ? `0 0 0 2.5px #378ADD` : "none",
        transition: "opacity 0.18s, box-shadow 0.18s",
        userSelect: "none",
        zIndex: isSelected ? 10 : 1,
      }}
    >
      {node.model?.name ?? `${t("Model")} #${node.id}`}
      <span
        style={{
          display: "block",
          fontSize: 10,
          fontWeight: 400,
          opacity: 0.65,
        }}
      >
        {t("Epoch")} {node.model?.epoch?.current} · #{node.id}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main RadialMindMap component
// ─────────────────────────────────────────────

const RadialMindMap = () => {
  const { id } = useParams();
  const [maxDepth, setMaxDepth] = useState(3);
  const [depthInput, setDepthInput] = useState("3");
  const [positions, setPositions] = useState<Record<number, NodePosition>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
  });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [inspModelList, setInspModelList] = useState<InspModelItem[]>([]);
  const [tab, setTab] = useState<TabType>("tree");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [singleSectionOpen, setSingleSectionOpen] = useState(true);
  const [treeSectionOpen, setTreeSectionOpen] = useState(true);
  const [treeOpen, setTreeOpen] = useState(false);

  const fetchInspModel = async () => {
    try {
      const response = await fetchInspModels();

      const byId = new Map<number, InspModelItem>(response.map((item: InspModelItem) => [item.id, item]));
      const picked = new Map<number, InspModelItem>();

      response
        .filter((item: InspModelItem) => parseInfo(item.info)?.project_id === Number(id))
        .forEach((item: InspModelItem) => {
          // 부모 모델은 project_id가 다르거나 info가 없을 수 있으므로 parentModel 체인을 따라 같이 포함
          let cur: InspModelItem | undefined = item;
          while (cur && !picked.has(cur.id)) {
            picked.set(cur.id, cur);
            cur = byId.get(parseInfo(cur.info)?.parentModel?.id);
          }
        });

      const filtered = [...picked.values()];
      setInspModelList(filtered);
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInspModel();
  }, [id]);

  const models: ParsedModel[] = useMemo(() => {
    const result: ParsedModel[] = inspModelList.map((item) => {
      const parsed = parseInfo(item.info) ?? {};
      return {
        ...item,
        epoch: parsed.epoch,
        validHistory_id: parsed.validHistory_id,
        parentId: parsed.parentModel?.id,
      };
    });

    console.log("models", result);
    return result;
  }, [inspModelList]);

  const childMap = useMemo(() => {
    const map: Record<number, number[]> = {};
    models.forEach((item) => {
      if (item.parentId) {
        if (!map[item.parentId]) map[item.parentId] = [];
        map[item.parentId].push(item.id);
      }
    });

    return map;
  }, [models]);

  const singleModels = useMemo(() => {
    return models.filter((model) => {
      const hasParent = !!model.parentId;
      const hasChild = !!childMap[model.id];
      console.log("models", models);

      return !hasParent && !hasChild;
    });
  }, [models, childMap]);

  const treeModels = useMemo(() => {
    const singleIds = new Set(singleModels.map((m) => m.id));
    console.log("singleIds", singleIds);

    const result = models.filter((m) => !singleIds.has(m.id));
    console.log("treeModels", result);
    return result;
  }, [models, singleModels]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 1800, h: 900 });

  useEffect(() => {
    if (!wrapRef.current) return;

    const update = () => {
      const { offsetWidth: w, offsetHeight: h } = wrapRef.current!;
      setCanvasSize({ w: Math.max(w * 2, 1800), h: Math.max(h * 2, 900) });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const CENTER_X = canvasSize.w / 2;
  const CENTER_Y = canvasSize.h / 2;

  // ── Build tree ──────────────────────────────
  const { nodeMap, roots } = useMemo(() => {
    const nodeMap = buildNodeMap(treeModels);
    const roots = getRoots(nodeMap, treeModels);
    console.log("roots", roots);
    return { nodeMap, roots };
  }, [treeModels]);

  // ── Assign radial positions ─────────────────
  useEffect(() => {
    const out: Record<number, NodePosition> = {};
    if (roots.length === 1) {
      assignRadialPositions(roots[0], 0, -Math.PI, Math.PI, CENTER_X, CENTER_Y, maxDepth, out);
    } else {
      const step = (Math.PI * 2) / roots.length;
      roots.forEach((root, i) => {
        const a0 = i * step - Math.PI;
        assignRadialPositions(root, 0, a0, a0 + step, CENTER_X, CENTER_Y, maxDepth - 1, out);
      });
    }
    setPositions(out);
    setSelectedId(null);
  }, [roots, maxDepth, CENTER_X, CENTER_Y]);

  // ── Center canvas after layout ──────────────
  useEffect(() => {
    if (!wrapRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = wrapRef.current;
    setPan({ x: w / 2 - CENTER_X, y: h / 2 - CENTER_Y });
  }, [CENTER_X, CENTER_Y]);

  // ── Collect visible edges ───────────────────
  const edges = useMemo<EdgeData[]>(() => {
    const result: EdgeData[] = [];
    function collect(node: TreeNode, depth: number) {
      if (depth >= maxDepth) return;
      node.children.forEach((child) => {
        if (positions[child.id]) {
          result.push({ fromId: node.id, toId: child.id, depth });
          collect(child, depth + 1);
        }
      });
    }
    roots.forEach((r) => collect(r, 0));
    return result;
  }, [roots, positions, maxDepth]);

  // ── Related IDs for highlight ───────────────
  const relatedIds = useMemo<Set<number>>(() => {
    if (selectedId === null) return new Set();
    const set = new Set<number>([selectedId]);
    // ancestors
    let cur = nodeMap[selectedId];
    while (cur?.model?.parentId && nodeMap[cur.model.parentId]) {
      set.add(cur.model.parentId);
      cur = nodeMap[cur.model.parentId];
    }
    // descendants
    function addDesc(n: TreeNode) {
      n.children.forEach((c) => {
        set.add(c.id);
        addDesc(c);
      });
    }
    if (nodeMap[selectedId]) addDesc(nodeMap[selectedId]);
    return set;
  }, [selectedId, nodeMap]);

  // ── Node drag ───────────────────────────────
  const handleNodeDrag = useCallback((id: number, dx: number, dy: number) => {
    setPositions((prev) => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: { ...prev[id], x: prev[id].x + dx, y: prev[id].y + dy },
      };
    });
  }, []);

  // Node click
  const handleNodeClick = useCallback((id: number) => {
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);

  //  Canvas pan
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-node]")) return;
      panRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
      setSelectedId(null);
    },
    [pan],
  );

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!panRef.current) return;
      setPan({
        x: panRef.current.panX + e.clientX - panRef.current.startX,
        y: panRef.current.panY + e.clientY - panRef.current.startY,
      });
    };
    const onUp = () => {
      panRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Depth input
  const handleDepthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDepthInput(raw);
    const v = parseInt(raw);
    if (!isNaN(v) && v >= 1 && v <= 8) setMaxDepth(v);
  };

  const visibleNodes = Object.entries(positions) as [string, NodePosition][];
  const nodeCount = visibleNodes.length;

  return (
    <Box sx={{ display: "flex", width: "100%", gap: 4, overflowX: "auto", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? 240 : 36,
            height: "100%",
            flexShrink: 0,
            borderRight: "1px solid var(--dk-border, #e0e0e0)",
            display: "flex",
            flexDirection: "column",
            background: "var(--dk-surface-2, #fafafa)",
            transition: "width 0.2s ease",
            overflow: "hidden",
          }}
        >
          {/* 사이드바 헤더 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: sidebarOpen ? "space-between" : "center",
              px: 1.5,
              py: 1,
              borderBottom: "1px solid var(--dk-border, #e0e0e0)",
              minHeight: 44,
              flexShrink: 0,
            }}
          >
            {sidebarOpen && (
              <Typography fontSize={13} fontWeight={500} color="text.secondary" noWrap>
                {t("ModelListTitle")}
              </Typography>
            )}
            <IconButton size="small" onClick={() => setSidebarOpen((v) => !v)}>
              {sidebarOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Box>

          {/* 사이드바 바디 */}
          {sidebarOpen && (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* 싱글 모델 섹션 */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { background: "var(--dk-surface-3, #f0f0f0)" },
                  }}
                  onClick={() => setSingleSectionOpen((v) => !v)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 5,
                        background: "#E6F1FB",
                        color: "#185FA5",
                      }}
                    >
                      {singleModels.length}
                    </Box>
                    <Typography fontSize={12} fontWeight={500} color="text.secondary">
                      {t("SingleModel")}
                    </Typography>
                  </Box>
                  {singleSectionOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  )}
                </Box>

                {singleSectionOpen && (
                  <Box sx={{ px: 1, pb: 1 }}>
                    {singleModels.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          py: 3,
                          px: 1,
                        }}
                      >
                        <AllInboxIcon sx={{ fontSize: 22, color: "text.disabled" }} />
                        <Typography fontSize={12} color="text.disabled" textAlign="center" lineHeight={1.4}>
                          {t("NoSingleModelMsg")}
                        </Typography>
                      </Box>
                    ) : (
                      singleModels.map((model) => (
                        <Box
                          key={model.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            cursor: "pointer",
                            "&:hover": {
                              background: "var(--dk-surface, #fff)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: "#378ADD",
                              flexShrink: 0,
                            }}
                          />
                          <Typography fontSize={13} noWrap sx={{ flex: 1 }}>
                            {model.name ?? model.id}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* 트리 모델 섹션 */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { background: "var(--dk-surface-3, #f0f0f0)" },
                  }}
                  onClick={() => setTreeSectionOpen((v) => !v)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 5,
                        background: "#EAF3DE",
                        color: "#3B6D11",
                      }}
                    >
                      {treeModels.length}
                    </Box>
                    <Typography fontSize={12} fontWeight={500} color="text.secondary">
                      {t("TreeModel")}
                    </Typography>
                  </Box>
                  {treeOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  )}
                </Box>

                {treeSectionOpen && (
                  <Box sx={{ px: 1, pb: 1 }}>
                    {treeModels.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          py: 3,
                          px: 1,
                        }}
                      >
                        <AllInboxIcon sx={{ fontSize: 22, color: "text.disabled" }} />
                        <Typography fontSize={12} color="text.disabled" textAlign="center" lineHeight={1.4}>
                          {t("NoTreeModelMsg")}
                        </Typography>
                      </Box>
                    ) : (
                      treeModels.map((model) => (
                        <Box
                          key={model.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            cursor: "pointer",
                            "&:hover": {
                              background: "var(--dk-surface, #fff)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: "#1D9E75",
                              flexShrink: 0,
                            }}
                          />
                          <Typography fontSize={13} noWrap sx={{ flex: 1 }}>
                            {model.name ?? model.id}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setTab("tree")}
              className={`px-4 py-2 font-medium ${
                tab === "tree" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"
              }`}
            >
              {t("Tree")}
            </button>
            <button
              onClick={() => setTab("mindmap")}
              className={`px-4 py-2 font-medium ${
                tab === "mindmap" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"
              }`}
            >
              {t("MindMap")}
            </button>
          </div>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: tab === "tree" ? "flex" : "none",
                flex: 1,
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {treeModels.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    gap: 1,
                    color: "text.disabled",
                  }}
                >
                  <AllInboxIcon sx={{ fontSize: 32 }} />
                  <Typography fontSize={13} color="text.disabled">
                    {t("NoTreeModelMsg")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    overflowX: "auto",
                    overflowY: "auto",
                    p: 3,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                    {roots.map((root) => (
                      <TreeItemNode key={root.id} node={root} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
            {/* 마인드맵 탭 */}
            <Box
              sx={{
                display: tab === "mindmap" ? "flex" : "none",
                flex: 1,
                flexDirection: "column",
              }}
            >
              {/* 기존 마인드맵 내용 그대로 */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                  fontFamily: "inherit",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "10px 16px",
                    borderBottom: "0.5px solid #e0dfd8",
                    flexWrap: "wrap",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, color: "var(--dk-text-3, #888)" }}>{t("DisplayDepth")}</span>
                    <input
                      type="number"
                      min={1}
                      max={8}
                      value={depthInput}
                      onChange={handleDepthChange}
                      style={{
                        width: 52,
                        height: 30,
                        textAlign: "center",
                        fontSize: 13,
                        border: "0.5px solid #c0bfb8",
                        borderRadius: 6,
                        background: "inherit",
                        color: "inherit",
                        outline: "none",
                      }}
                    />
                    <span style={{ fontSize: 12, color: "var(--dk-text-3, #aaa)" }}>
                      {t("Node")} {nodeCount}
                      {t("CountUnit")}
                    </span>
                  </div>

                  {/* Legend */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginLeft: "auto",
                      flexWrap: "wrap",
                    }}
                  >
                    {[
                      { color: "#378ADD", label: t("Root") },
                      { color: "#7F77DD", label: t("Level1") },
                      { color: "#1D9E75", label: t("Level2") },
                      { color: "#BA7517", label: t("Level3Plus") },
                    ].map(({ color, label }) => (
                      <div
                        key={label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          color: "var(--dk-text-3, #888)",
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: color,
                            flexShrink: 0,
                          }}
                        />
                        {label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Canvas ── */}
                <div
                  ref={wrapRef}
                  onMouseDown={handleCanvasMouseDown}
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "grab",
                    minHeight: 480,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: canvasSize.w,
                      height: canvasSize.h,
                      transform: `translate(${pan.x}px, ${pan.y}px)`,
                      transformOrigin: "0 0",
                    }}
                  >
                    <EdgeLayer edges={edges} positions={positions} w={canvasSize.w} h={canvasSize.h} />

                    {visibleNodes.map(([idStr, pos]) => {
                      const id = parseInt(idStr);
                      const node = nodeMap[id];
                      if (!node) return null;
                      return (
                        <MindMapNode
                          key={id}
                          node={node}
                          pos={pos}
                          isDimmed={selectedId !== null && !relatedIds.has(id)}
                          isSelected={selectedId === id}
                          onMouseEnter={(e, model) =>
                            setTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              model,
                            })
                          }
                          onMouseLeave={() => setTooltip((t) => ({ ...t, visible: false }))}
                          onClick={handleNodeClick}
                          onDragMove={handleNodeDrag}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* ── Info bar ── */}
                <div
                  style={{
                    padding: "6px 16px",
                    fontSize: 11,
                    color: "var(--dk-text-3, #aaa)",
                    borderTop: "0.5px solid #e0dfd8",
                    display: "flex",
                    gap: 16,
                    flexShrink: 0,
                  }}
                >
                  <span>{t("NodeDragHelpMsg")}</span>
                  <span>{t("BackgroundDragHelpMsg")}</span>
                  <span>{t("NodeClickHelpMsg")}</span>
                </div>

                <Tooltip state={tooltip} />
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RadialMindMap;

const TreeItemNode: React.FC<{ node: TreeNode; isRoot?: boolean }> = ({ node, isRoot = false }) => {
  const modelData = node.model;
  const depth = isRoot ? 0 : undefined;

  const depthStyle = isRoot
    ? { bg: "#E6F1FB", border: "#378ADD", color: "#0C447C" }
    : {
        bg: "#fafafa",
        border: "var(--dk-border, #ddd)",
        color: "var(--dk-text, #333)",
      };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* 노드 카드 */}
      <MuiTooltip
        placement="top"
        title={
          modelData ? (
            <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
              <div>
                {t("ID")}: {node.id}
              </div>
              <div>
                {t("Epoch")}: {modelData.epoch?.current}
              </div>
              <div>validHistory_id: {modelData.validHistory_id}</div>
              {modelData.parentId && (
                <div>
                  {t("ParentID")}: {modelData.parentId}
                </div>
              )}
            </Box>
          ) : (
            ""
          )
        }
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            border: `1.5px solid ${isRoot ? "#378ADD" : "#c0bfb8"}`,
            borderRadius: "20px",
            background: isRoot ? "#E6F1FB" : "#fff",
            cursor: "default",
            whiteSpace: "nowrap",
            textAlign: "center",
            minWidth: 120,
            boxShadow: isRoot ? "0 0 0 3px #E6F1FB" : "none",
            transition: "box-shadow 0.15s",
            "&:hover": {
              boxShadow: `0 0 0 3px ${isRoot ? "#B5D4F4" : "#e0dfd8"}`,
            },
          }}
        >
          <Typography fontSize={13} fontWeight={500} color={isRoot ? "#0C447C" : "text.primary"} noWrap>
            {modelData?.name ?? `${t("Model")} #${node.id}`}
          </Typography>
          <Typography fontSize={11} color={isRoot ? "#185FA5" : "text.disabled"} noWrap>
            {t("Epoch")} {modelData?.epoch?.current} · #{node.id}
          </Typography>
        </Box>
      </MuiTooltip>

      {node.children.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: "1px", height: 24, background: "#c0bfb8" }} />
          {node.children.length === 1 ? (
            <TreeItemNode node={node.children[0]} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    right: 0,
                    height: "1px",
                    background: "transparent",
                  }}
                />
                {node.children.map((child, idx) => (
                  <Box
                    key={child.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      px: 2,
                      position: "relative",
                    }}
                  >
                    <Box sx={{ width: "1px", height: 24, background: "#c0bfb8" }} />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: idx === 0 ? "50%" : 0,
                        right: idx === node.children.length - 1 ? "50%" : 0,
                        height: "1px",
                        background: "#c0bfb8",
                      }}
                    />
                    <TreeItemNode node={child} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
